import { useState } from "react";
import { Mail, MessageCircle, BookOpen, Send, CheckCircle2 } from "lucide-react";
import { trackCta, trackEvent } from "@/lib/analytics";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";

export default function Support() {
  const { t } = useT();
  useSEO({
    title: "Support | Contact StoicOs & Virtue Guide",
    description: "Get help with Virtue Guide. We're a small team and read every message ourselves. Contact us, press inquiries, and answers to common questions.",
    canonical: "https://stoicos.ai/support",
    jsonLd: [
      webPage({ name: "Support", description: "Contact StoicOs and read FAQs about Virtue Guide.", url: "/support", breadcrumbItems: [{ name: "Home", url: "/" }, { name: "Support", url: "/support" }] }),
      breadcrumb([{ name: "Home", url: "/" }, { name: "Support", url: "/support" }]),
    ],
  });
  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });
  const [sent, setSent] = useState(false);

  const faqs = [
    { q: t("support.faq1q"), a: t("support.faq1a") },
    { q: t("support.faq2q"), a: t("support.faq2a") },
    { q: t("support.faq3q"), a: t("support.faq3a") },
    { q: t("support.faq4q"), a: t("support.faq4a") },
  ];

  const channels = [
    { icon: Mail,          label: t("support.emailLabel"),   value: "ek@stoicos.ai",          href: "mailto:ek@stoicos.ai",  testid: "support-channel-email",   track: "contact_email_card" },
    { icon: MessageCircle, label: t("support.pressLabel"),   value: "ek@stoicos.ai",          href: "mailto:ek@stoicos.ai",  testid: "support-channel-press",   track: "contact_press_card" },
    { icon: BookOpen,      label: t("support.readLabel"),    value: t("support.privacyValue"), href: "/privacy",              testid: "support-channel-privacy", track: "privacy_policy_card" },
  ];

  const onSubmit = (e) => {
    e.preventDefault();
    trackEvent("contact_form_submit", { topic: form.topic, page_path: "/support" });
    // Static page — open mail client with prefilled message
    const subject = encodeURIComponent(`[${form.topic.toUpperCase()}] Support: ${form.name || "StoicOs"}`);
    const body = encodeURIComponent(`${form.message}\n\n${form.name}\n${form.email}`);
    window.location.href = `mailto:ek@stoicos.ai?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <main data-testid="support-page" className="relative pt-32 md:pt-40 pb-32">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-8">{t("support.eyebrow")}</p>
        <h1 className="font-serif text-5xl md:text-7xl text-bone tracking-tight leading-[1.0]">
          {t("support.headline1")}<br/><span className="italic text-gold">{t("support.headline2")}</span>
        </h1>
        <p className="mt-8 max-w-xl text-bone/70 font-light text-lg leading-relaxed">
          {t("support.intro")}
        </p>

        <div className="gold-rule my-16" />

        {/* Channels */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5 mb-20">
          {channels.map((c) => (
            <a key={c.testid} href={c.href} onClick={() => trackCta(c.track, { placement: "support_channels" })} data-testid={c.testid} className="bg-ink p-8 hover:bg-[#0A0907] transition-colors block group">
              <c.icon size={22} color="#C5A059" strokeWidth={1.5} />
              <p className="mt-6 text-[10px] tracking-[0.25em] uppercase text-fog">{c.label}</p>
              <p className="mt-2 font-serif text-2xl text-bone group-hover:text-gold transition-colors">{c.value}</p>
            </a>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Contact form */}
          <div className="lg:col-span-7">
            <h2 className="font-serif text-3xl md:text-4xl text-bone tracking-tight mb-2">{t("support.writeTitle")}</h2>
            <p className="text-fog font-light mb-10">{t("support.writeSub")}</p>

            {sent ? (
              <div data-testid="support-form-sent" className="border border-gold/30 bg-gold/5 p-8 flex items-start gap-4">
                <CheckCircle2 size={24} color="#C5A059" strokeWidth={1.5} />
                <div>
                  <p className="font-serif text-2xl text-bone">{t("support.sentTitle")}</p>
                  <p className="mt-2 text-fog font-light">{t("support.sentBody")}<a className="text-gold gold-link" href="mailto:ek@stoicos.ai">ek@stoicos.ai</a>.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="support-form" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label={t("support.fieldName")}>
                    <input
                      data-testid="support-input-name"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full bg-transparent border-b border-white/10 focus:border-gold outline-none py-3 text-bone placeholder:text-fog/50"
                      placeholder={t("support.placeholderName")}
                    />
                  </Field>
                  <Field label={t("support.fieldEmail")}>
                    <input
                      data-testid="support-input-email"
                      required type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full bg-transparent border-b border-white/10 focus:border-gold outline-none py-3 text-bone placeholder:text-fog/50"
                      placeholder="you@example.com"
                    />
                  </Field>
                </div>

                <Field label={t("support.fieldTopic")}>
                  <select
                    data-testid="support-input-topic"
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className="w-full bg-ink border border-white/10 focus:border-gold outline-none py-3 px-3 text-bone"
                  >
                    <option value="general">{t("support.topicGeneral")}</option>
                    <option value="account">{t("support.topicAccount")}</option>
                    <option value="bug">{t("support.topicBug")}</option>
                    <option value="press">{t("support.topicPress")}</option>
                    <option value="other">{t("support.topicOther")}</option>
                  </select>
                </Field>

                <Field label={t("support.fieldMessage")}>
                  <textarea
                    data-testid="support-input-message"
                    required rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-transparent border-b border-white/10 focus:border-gold outline-none py-3 text-bone placeholder:text-fog/50 resize-none"
                    placeholder={t("support.placeholderMessage")}
                  />
                </Field>

                <button
                  type="submit"
                  data-testid="support-submit"
                  className="inline-flex items-center gap-3 bg-gold hover:bg-gold-hover text-ink px-8 py-4 text-[12px] tracking-[0.2em] uppercase font-medium transition-colors"
                >
                  {t("support.submit")} <Send size={14} strokeWidth={1.5} />
                </button>
              </form>
            )}
          </div>

          {/* FAQ */}
          <div className="lg:col-span-5">
            <h2 className="font-serif text-3xl md:text-4xl text-bone tracking-tight mb-10">{t("support.faqTitle")}</h2>
            <div className="space-y-8">
              {faqs.map((f, i) => (
                <div key={i} data-testid={`support-faq-${i}`} className="border-l border-gold/30 pl-6">
                  <h3 className="font-serif text-xl text-bone leading-snug">{f.q}</h3>
                  <p className="mt-3 text-fog font-light leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.25em] uppercase text-gold mb-2 block">{label}</span>
      {children}
    </label>
  );
}
